import { useTranslation } from "react-i18next";
import { BreadcrumbJsonLd } from "@/src/common/components/breadcrumb-jsonld";
import { JsonLd } from "@/src/common/components/json-ld";
import { PageHelmet } from "@/src/common/components/page-helmet";
import { env } from "@/src/common/constants/env";
import { DocumentPageShell } from "../components/document-page-shell";
import { LEGAL_INFORMATION } from "../constants/legal";
import { MARKETING_ROUTES } from "../constants/marketing-routes";

function TermsSection({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<section className="rounded-4xl border border-white/10 bg-white/5 p-6 shadow-[0_24px_70px_-48px_rgba(0,0,0,0.78)] backdrop-blur sm:p-8">
			<h2 className="text-2xl font-semibold tracking-[-0.04em] text-white">{title}</h2>
			<div className="mt-5 space-y-4 text-sm leading-7 text-white/72">{children}</div>
		</section>
	);
}

export default function TermsPage() {
	const { t } = useTranslation("marketing");
	const pageUrl = `${env.SITE_URL}${MARKETING_ROUTES.TERMS}`;

	return (
		<>
			<PageHelmet title="Conditions d'utilisation" description="Conditions d'utilisation du service LookDat." />
			<BreadcrumbJsonLd
				items={[
					{ name: t("breadcrumb_home"), url: env.SITE_URL },
					{ name: t("nav_terms"), url: pageUrl },
				]}
			/>
			<JsonLd
				data={{
					"@context": "https://schema.org",
					"@type": "WebPage",
					name: "Conditions d'utilisation",
					url: pageUrl,
					inLanguage: "fr",
					isPartOf: { "@id": `${env.SITE_URL}/#website` },
				}}
			/>
			<DocumentPageShell
				eyebrow="Terms"
				title="Conditions d'utilisation"
				description="Ces conditions encadrent l'accès et l'utilisation de LookDat. En utilisant le service, tu acceptes les présentes conditions."
			>
				<TermsSection title="1. Objet du service">
					<p>
						LookDat est un service permettant de créer et gérer des espaces privés de partage pour des groupes, notamment autour de photos,
						vidéos, stories, commentaires, réactions et messages privés.
					</p>
				</TermsSection>

				<TermsSection title="2. Éligibilité">
					<p>
						L'utilisation du service est réservée aux personnes âgées d'au moins <strong>13 ans</strong>.
					</p>
					<p>Si tu utilises le service au nom d'une organisation ou d'un groupe, tu garantis disposer des autorisations nécessaires.</p>
				</TermsSection>

				<TermsSection title="3. Compte et sécurité">
					<p>Tu es responsable des informations transmises lors de la création de ton compte et de la confidentialité de tes accès.</p>
					<p>Tu t'engages à signaler sans délai toute utilisation non autorisée de ton compte ou toute atteinte à la sécurité.</p>
				</TermsSection>

				<TermsSection title="4. Règles d'usage">
					<p>Tu t'engages à ne pas utiliser LookDat pour :</p>
					<ul className="list-disc space-y-2 pl-5">
						<li>
							publier, transmettre ou stocker des contenus illicites, diffamatoires, haineux, violents, trompeurs ou portant atteinte aux
							droits d'autrui ;
						</li>
						<li>harceler, menacer, usurper l'identité d'un tiers ou porter atteinte à sa vie privée ;</li>
						<li>envoyer du spam, détourner le service, contourner les mesures de sécurité ou perturber son fonctionnement ;</li>
						<li>partager sans autorisation des contenus privés ou protégés par des droits.</li>
					</ul>
				</TermsSection>

				<TermsSection title="5. Contenus et responsabilité">
					<p>Tu restes seul responsable des contenus, messages, médias et informations que tu publies ou transmets via LookDat.</p>
					<p>
						LookDat agit comme un hébergeur technique du contenu publié par les utilisateurs et n'assume aucune responsabilité éditoriale
						sur les contenus partagés par les utilisateurs, sous réserve des obligations légales applicables.
					</p>
					<p>
						Nous pouvons retirer, suspendre ou limiter l'accès à des contenus ou comptes en cas de violation des présentes conditions, de
						risque pour la sécurité ou d'obligation légale.
					</p>
				</TermsSection>

				<TermsSection title="6. Disponibilité du service">
					<p>
						Nous faisons nos meilleurs efforts pour assurer la disponibilité du service, sans garantir une disponibilité continue, sans
						erreur ni interruption.
					</p>
					<p>Nous pouvons modifier, suspendre ou faire évoluer tout ou partie du service à tout moment.</p>
				</TermsSection>

				<TermsSection title="7. Limitation de responsabilité">
					<p>
						Dans les limites permises par la loi, LookDat ne pourra être tenu responsable des dommages indirects, immatériels ou consécutifs
						liés à l'utilisation ou à l'impossibilité d'utiliser le service.
					</p>
					<p>
						En tout état de cause, la responsabilité de l'éditeur ne saurait être engagée pour les contenus publiés par les utilisateurs,
						pour les actions de tiers ou pour les dommages résultant d'un usage non conforme du service.
					</p>
				</TermsSection>

				<TermsSection title="8. Données personnelles">
					<p>Le traitement des données personnelles est encadré par la politique de confidentialité disponible sur le site.</p>
				</TermsSection>

				<TermsSection title="9. Droit applicable et juridiction">
					<p>Les présentes conditions sont régies principalement par le droit français.</p>
					<p>
						Le service peut être accessible dans le monde entier, mais tout litige relatif à son utilisation sera interprété conformément au
						droit français, sous réserve des dispositions impératives applicables dans ton pays de résidence.
					</p>
				</TermsSection>

				<TermsSection title="10. Contact">
					<p>
						Pour toute question relative à ces conditions, contacte <strong>{LEGAL_INFORMATION.legalEmail}</strong>.
					</p>
				</TermsSection>
			</DocumentPageShell>
		</>
	);
}
